import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FaLock } from 'react-icons/fa';

import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import ScrollReveal from '../components/ScrollReveal';
import QRCodeContent from '../components/QRCodeContent';
import { useSeason } from '../contexts/SeasonContext';
import { FIXED_H } from '../components/layoutConstants';
import { publicApi } from '../api';
import { trackInscriptionClick } from '../utils/analytics';

// Repli tant que l'admin n'a pas encore renseigné de documents dans l'onglet "Documents"
const STATIC_DOCUMENTS = [
  { href: require('../data/documents/ffbad-2026-2027-formulaire-de-prise-de-licence-adulte-a4-07.pdf'), label: 'Formulaire de prise de licence ADULTES 2026/2027' },
  { href: require('../data/documents/ffbad-2026-2027-formulaire-de-prise-de-licence-mineur-a4-05.pdf'), label: 'Formulaire de prise de licence MINEURS 2026/2027' },
  { href: require('../data/documents/FFBaD_-_ADULTES_QUESTIONNAIRE_SANTE.pdf'), label: 'Questionnaire de santé — Adultes' },
  { href: require('../data/documents/FFBaD_-_MINEURS_QUESTIONNAIRE_SANTE.pdf'), label: 'Questionnaire de santé — Mineurs' },
  { href: require('../data/documents/GUI21F1_ADM_Certificat de non contre-indication.pdf'), label: 'Formulaire certificat médical' },
];

// Repli tant que l'admin n'a pas encore renseigné le lien dans l'onglet "Documents"
const STATIC_PASS_SPORT = {
  message:    "Pass'Sport reconduit — montant porté à 70 €",
  link_label: "Activer votre Pass'Sport",
  link_url:   'https://www.pass.sports.gouv.fr',
};

const tabDefs = [
  { key: 'modalite',    label: 'Modalités',    eyebrow: "Comment s'inscrire",  title: "Modalités d'Inscription" },
  { key: 'passSport',  label: "Pass'Sport",   eyebrow: 'Aide financière',     title: "Pass'Sport"              },
  { key: 'couponSport',label: 'Coupon Sport',  eyebrow: 'Moyen de paiement',   title: 'Coupon Sport ANCV'       },
  { key: 'qrCode',     label: 'QR Code',      eyebrow: 'Accès rapide',        title: 'QR Code'                 },
];

const InfoBox = ({ variant = 'brand', children }) => {
  const styles = {
    brand:   'bg-brand-50 border-brand-200 text-brand-900',
    success: 'bg-green-50 border-green-200 text-green-900',
    neutral: 'bg-stone-50 border-stone-200 text-stone-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return <div className={`border rounded-xl p-5 text-sm leading-relaxed ${styles[variant]}`}>{children}</div>;
};

const DocLink = ({ href, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-800 hover:underline">
    {label} →
  </a>
);

const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const InscriptionsFermees = ({ season, nextSeason }) => (
  <div className="flex items-start gap-4 p-5 rounded-2xl bg-stone-950 text-white mb-8">
    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
      <FaLock size={16} className="text-stone-300" />
    </div>
    <div>
      <p className="font-semibold text-sm mb-1">Inscriptions fermées</p>
      <p className="text-xs text-stone-400 leading-relaxed">
        Les inscriptions{season?.description ? ` pour la ${season.description}` : ''} sont actuellement fermées.
        {nextSeason?.inscription_start && (
          <> Pour la <strong className="text-white">{nextSeason.description}</strong>, les inscriptions ouvriront le <strong className="text-white">{fmtDate(nextSeason.inscription_start)}</strong>.</>
        )}
      </p>
    </div>
  </div>
);

const Inscription = ({ setCurrentSection, currentSection }) => {
  const { tab }     = useParams();
  const navigate    = useNavigate();
  const stickyRef   = useRef(null);
  const sectionRefs = useRef({});
  const activeRef   = useRef(null);
  const isProg      = useRef(false);
  const [active, setActive] = useState(tab || 'modalite');

  const { season, nextSeason } = useSeason();
  const inscriptionsOpen = season?.inscriptions_open ?? true;
  const [documents, setDocuments] = useState(STATIC_DOCUMENTS);
  const [passSport, setPassSport] = useState(STATIC_PASS_SPORT);

  useEffect(() => {
    publicApi.get('inscription_documents')
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setDocuments(data.map(d => ({ href: d.file_url, label: d.label })));
        }
      })
      .catch(() => {}); // garde les documents statiques en cas d'erreur
  }, []);

  useEffect(() => {
    publicApi.get('pass_sport')
      .then(data => {
        if (data) {
          setPassSport({
            message:    data.message    || STATIC_PASS_SPORT.message,
            link_label: data.link_label || STATIC_PASS_SPORT.link_label,
            link_url:   data.link_url   || STATIC_PASS_SPORT.link_url,
          });
        }
      })
      .catch(() => {}); // garde le contenu statique en cas d'erreur
  }, []);
  // Année de référence du Pass'Sport (rentrée de septembre N, valable du 1er juillet au 31 décembre N).
  const passSportYear = season?.start_date ? new Date(season.start_date).getFullYear() : new Date().getFullYear();

  const scrollOffset = useCallback(() => FIXED_H + (stickyRef.current?.offsetHeight ?? 0), []);

  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - scrollOffset(), behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    setActive(key);
    navigate(`/inscription/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  useEffect(() => {
    const key = tab || 'modalite';
    if (key !== 'modalite') { const t = setTimeout(() => goTo(key, 'instant'), 120); return () => clearTimeout(t); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      let current = tabDefs[0].key;
      for (const { key } of tabDefs) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) { activeRef.current = current; setActive(current); navigate(`/inscription/${current}`, { replace: true }); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const ref = (key) => (el) => { sectionRefs.current[key] = el; };
  const meta = tabDefs.find(t => t.key === active);

  return (
    <>
      <PageHeader
        title="Inscription"
        subtitle={season?.description ? `Rejoignez le BCE — ${season.description}` : 'Rejoignez le Badminton Club Éragny'}
        setCurrentSection={setCurrentSection}
        currentSection={currentSection}
      />

      <div ref={stickyRef} className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]" style={{ top: FIXED_H }}>
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }} className="flex items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">{meta?.eyebrow}</span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">{meta?.title}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* Modalités */}
      <div ref={ref('modalite')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Comment s'inscrire</span>
              <h2 className="display-md">Modalités d'Inscription</h2>
            </ScrollReveal>
            {!inscriptionsOpen && <InscriptionsFermees season={season} nextSeason={nextSeason} />}
            <div className="space-y-6 max-w-2xl">
              <p className="text-stone-600 leading-relaxed">
                {inscriptionsOpen
                  ? "Les inscriptions sont ouvertes. Que vous soyez débutant ou joueur confirmé, le Badminton Club Éragny vous accueille. Deux modes d'inscription sont proposés :"
                  : "Les inscriptions pour cette saison sont actuellement fermées. Consultez cette page lors de l'ouverture des inscriptions."
                }
              </p>
              {inscriptionsOpen && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="card-minimal p-5 space-y-3">
                    <h3 className="font-semibold text-stone-950 text-sm">Inscription en ligne</h3>
                    <p className="text-sm text-stone-500">
                      Paiement par carte bancaire uniquement, possible en 1, 2 ou 3 fois.
                    </p>
                    <a href="https://adherer.ffbad.club/bce95" target="_blank" rel="noopener noreferrer" onClick={trackInscriptionClick} className="btn-brand inline-block text-sm">
                      S'inscrire en ligne
                    </a>
                  </div>
                  <div className="card-minimal p-5 space-y-3">
                    <h3 className="font-semibold text-stone-950 text-sm">Inscription papier</h3>
                    <p className="text-sm text-stone-500">
                      Pour tout autre moyen de paiement (chèque, espèces, Coupon Sport ANCV…). Formulaires disponibles ci-dessous, dans la rubrique "Documents". <b>Seuls les dossiers complets seront acceptés.</b>
                    </p>
                    <DocLink href="https://www.ffbad.org/pratiquer-se-licencier-licence" label="En cas de problème, infos sur le site de la FFBAD" />
                  </div>
                </div>
              )}
              <InfoBox variant="warning">
                <p className="font-semibold mb-1">Compte myFFBad</p>
                <p className="text-sm">
                  Si vous possédez déjà un compte myFFBad, connectez-vous avant de commencer votre inscription en ligne : vos informations viendront alimenter automatiquement la fiche d'inscription.
                  Si vous êtes déjà licencié(e) dans un autre club, cette connexion est <strong>obligatoire</strong> pour récupérer votre licence et bénéficier du tarif "licencié autre club".
                </p>
              </InfoBox>
              <div className="space-y-3">
                <h3 className="font-semibold text-stone-950 text-sm">Documents</h3>
                {documents.map((d, i) => (
                  <div key={i} className="card-minimal p-4"><DocLink {...d} /></div>
                ))}
              </div>
              <InfoBox variant="brand">
                <p className="font-semibold mb-1">Nouveauté : certificat médical simplifié</p>
                <p className="text-brand-700 text-sm">Le certificat médical n'est plus obligatoire si vous répondez "non" à toutes les questions du questionnaire de santé.</p>
              </InfoBox>
              <InfoBox variant="neutral">
                <p className="font-semibold mb-1">Politique de remboursement</p>
                <p className="text-sm">Aucun remboursement n'est possible après validation de l'inscription, quel qu'en soit le motif.</p>
              </InfoBox>
            </div>
          </div>
        </section>
      </div>

      {/* Pass'Sport */}
      <div ref={ref('passSport')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Aide financière</span>
              <h2 className="display-md">Pass'Sport</h2>
            </ScrollReveal>
            <div className="space-y-5 max-w-2xl">
              <InfoBox variant="success">
                <p className="font-semibold mb-1">{passSport.message}</p>
                <DocLink href={passSport.link_url} label={passSport.link_label} />
              </InfoBox>
              {[
                { q: "Qu'est-ce que le Pass'Sport ?", a: `Le Pass'Sport est une allocation de rentrée sportive permettant de prendre une adhésion dès septembre ${passSportYear} pour l'année ${passSportYear}-${passSportYear + 1}.` },
                { q: 'Qui est concerné ?',             a: `Les enfants de 6 à 17 ans révolus au 30 juin ${passSportYear} bénéficiant de l'ARS, de l'AEEH ou de l'AAH (entre 16 et 18 ans).` },
                { q: 'Validité du dispositif',         a: `Le coupon est valable du 1er juillet ${passSportYear} au 31 décembre ${passSportYear}.` },
              ].map((item, i) => (
                <div key={i} className="card-minimal p-5">
                  <h3 className="font-semibold text-stone-950 text-sm mb-1.5">{item.q}</h3>
                  <p className="text-sm text-stone-500">{item.a}</p>
                </div>
              ))}
              <InfoBox variant="brand">
                <p className="font-semibold mb-2">Comment utiliser votre Pass'Sport</p>
                <ol className="space-y-1 text-sm">
                  <li><span className="font-semibold text-brand-800">1.</span> Lors de votre inscription en ligne, sélectionnez "Pass'Sport"</li>
                  <li><span className="font-semibold text-brand-800">2.</span> Ajoutez votre Pass'Sport en tant que justificatif</li>
                </ol>
              </InfoBox>
              {inscriptionsOpen && (
                <div className="flex flex-wrap gap-3">
                  <a href="https://www.pass.sports.gouv.fr/" target="_blank" rel="noopener noreferrer" className="btn-ghost">Activer votre Pass'Sport</a>
                  <a href="https://adherer.ffbad.club/bce95" target="_blank" rel="noopener noreferrer" onClick={trackInscriptionClick} className="btn-brand">S'inscrire avec le Pass'Sport</a>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Coupon Sport */}
      <div ref={ref('couponSport')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Moyen de paiement</span>
              <h2 className="display-md">Coupon Sport ANCV</h2>
            </ScrollReveal>
            <div className="space-y-5 max-w-2xl">
              <p className="text-stone-600 leading-relaxed text-sm">
                Le Badminton Club Éragny accepte les Coupons Sport ANCV comme moyen de paiement pour votre adhésion.
              </p>
              <InfoBox variant="warning">
                <p className="font-semibold mb-1">Paiement CB uniquement en ligne</p>
                <p className="text-sm">L'inscription en ligne n'accepte que le paiement par carte bancaire. Pour régler avec des Coupons Sport ANCV, l'inscription doit se faire en version papier — seuls les dossiers complets seront acceptés.</p>
              </InfoBox>
              <InfoBox variant="brand">
                <p className="font-semibold mb-2">Comment utiliser vos Coupons Sport</p>
                <ol className="space-y-1 text-sm">
                  <li><span className="font-semibold text-brand-800">1.</span> Téléchargez le formulaire de licence papier (onglet "Modalités", rubrique Documents)</li>
                  <li><span className="font-semibold text-brand-800">2.</span> Joignez vos Coupons Sport ANCV au dossier complet</li>
                  <li><span className="font-semibold text-brand-800">3.</span> Déposez le dossier auprès d'un responsable du club</li>
                </ol>
                <div className="mt-3">
                  <DocLink href="https://www.ffbad.org/pratiquer-se-licencier-licence" label="En cas de problème, infos sur le site de la FFBAD" />
                </div>
              </InfoBox>
              <p className="text-sm text-stone-400">
                Question ? <a href="mailto:secretariatbceragny@gmail.com" className="text-brand-800 hover:underline">secretariatbceragny@gmail.com</a>
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* QR Code */}
      <div ref={ref('qrCode')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Accès rapide</span>
              <h2 className="display-md">QR Code</h2>
            </ScrollReveal>
            <QRCodeContent />
          </div>
        </section>
      </div>
    </>
  );
};

export default Inscription;
